import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';
import { MatTableModule } from '@angular/material/table';
import { IconicModule } from 'app/shared/components/iconic/iconic.module';
import { ScrollViewModule } from 'app/shared/components/scroll-view/scroll-view.module';
import { BlockModalDragModule } from 'app/shared/directives/block-modal-drag/block-modal-drag.module';
import { ScreenService } from 'app/shared/services/screen.service';
import { TranslationService } from 'app/shared/services/translation.service';
import { NetworkResultItemLike } from '../../../../available-coverage.service';

export interface ColumnItemLike {
  name: string;
  alias?: string;
  width?: string;
  minWidth?: string;
  align?: 'left' | 'center' | 'right';
}

const columnsAliasMap = new Map<string, string>([
  ['operator', 'Operador'],
  ['technology', 'Tecnologia'],
  ['downloadSpeed', 'Download (Mbps)'],
  ['uploadSpeed', 'Upload (Mbps)'],
]);

@Component({
  selector: 'app-results-table-view',
  templateUrl: 'results-table-view.component.html',
  styleUrls: ['results-table-view.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    ScrollViewModule,
    BlockModalDragModule,
    IconicModule,
  ],
})
export class ResultsTableViewComponent {
  private _columns: ColumnItemLike[];
  @Input()
  set columns(value) {
    if (this._columns !== value) {
      this._columns = value;

      this.displayedColumns = [
        ...this._columns.map((x) => x.name),
        'expandControl',
      ];
    }
  }

  get columns() {
    return this._columns;
  }

  @Input()
  results: any[];

  displayedColumns: string[] = [];

  expandedItem: NetworkResultItemLike<any>;

  private _subItemsMap = new WeakMap<
    NetworkResultItemLike<any>,
    NetworkResultItemLike<any>[]
  >();
  private _subItemsIndexMap = new WeakMap<NetworkResultItemLike<any>, number>();

  isMobileObservable = this.screenService.observeIsMobile();

  constructor(
    private screenService: ScreenService,
    private translationService: TranslationService
  ) {}

  get noDataText(): string {
    return this.translationService.getCurrentLanguage()['no-data'];
  }

  onRowClick(item: NetworkResultItemLike<any>) {
    if (!this.isExpandable(item)) {
      return;
    }

    this.toggleExpand(item);
  }

  getAlias(column: ColumnItemLike) {
    return column.alias ?? columnsAliasMap.get(column.name) ?? column.name;
  }

  isExpanded(item: NetworkResultItemLike<any>): boolean {
    return this.expandedItem === item;
  }

  isExpandable(item: NetworkResultItemLike<any>): boolean {
    return !!item?.children?.length;
  }

  whenExpandable = (_, item: NetworkResultItemLike<any>) =>
    this.isExpandable(item);

  toggleExpand(item: NetworkResultItemLike<any>) {
    if (!this.isExpanded(item)) {
      this.expandedItem = item;
    } else {
      this.expandedItem = null;
    }
  }

  getChildren(item: NetworkResultItemLike<any>) {
    let items = this._subItemsMap.get(item);
    if (!items) {
      items = [item, ...item.children];
      this._subItemsMap.set(item, items);
    }

    return items;
  }

  getIndexInChildren(
    parent: NetworkResultItemLike<any>,
    item: NetworkResultItemLike<any>
  ) {
    let index = this._subItemsIndexMap.get(item);
    if (typeof index !== 'number') {
      index = this.getChildren(parent).indexOf(item);
      this._subItemsIndexMap.set(item, index);
    }

    return index;
  }
}
